import { useState, useRef, useEffect } from 'react';
import { Upload, X, Image, Link2, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUploader = ({ currentImage, onImageChange, label = "Imagen", isUserUpload = false, removeBg = false }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState('');
  const [mode, setMode] = useState('upload'); // 'upload' or 'url'
  const [urlInput, setUrlInput] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (currentImage) {
      setPreview(currentImage);
      // Detect if current image is a URL or uploaded file
      if (currentImage.startsWith('http') && !currentImage.includes('/api/uploads/')) {
        setMode('url');
        setUrlInput(currentImage);
      }
    }
  }, [currentImage]);

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('data:')) return url;
    // Handle both old /uploads/ and new /api/uploads/ paths
    if (url.startsWith('/uploads/')) {
      return `${process.env.REACT_APP_BACKEND_URL}/api${url}`;
    }
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = isUserUpload ? `${API}/upload/user` : `${API}/upload${removeBg ? '?remove_bg=true' : ''}`;
      const response = await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;
      setPreview(imageUrl);
      onImageChange(imageUrl);
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error al subir la imagen');
      setPreview(currentImage || '');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (!urlInput.trim()) {
      toast.error('Ingresa una URL válida');
      return;
    }

    // Basic URL validation
    if (!urlInput.startsWith('http://') && !urlInput.startsWith('https://')) {
      toast.error('La URL debe comenzar con http:// o https://');
      return;
    }

    setPreview(urlInput);
    onImageChange(urlInput);
    toast.success('URL de imagen guardada');
  };

  const handleRemove = () => {
    setPreview('');
    setUrlInput('');
    onImageChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const toggleMode = () => {
    setMode(mode === 'upload' ? 'url' : 'upload');
  };

  const displayUrl = preview ? getFullUrl(preview) : '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700">{label}</label>
        <button
          type="button"
          onClick={toggleMode}
          className="flex items-center gap-2 text-xs text-royal hover:text-royal-hover transition-colors"
        >
          {mode === 'upload' ? (
            <>
              <Upload className="w-3 h-3" />
              Subir archivo
            </>
          ) : (
            <>
              <Link2 className="w-3 h-3" />
              Usar URL
            </>
          )}
          <span className="text-slate-400">|</span>
          <span className="text-slate-500">Cambiar a {mode === 'upload' ? 'URL' : 'subir'}</span>
        </button>
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {displayUrl ? (
        <div className="relative group">
          <img
            src={displayUrl}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border border-slate-200 bg-slate-100"
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">Error al cargar</text></svg>';
            }}
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            {mode === 'upload' ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="w-4 h-4 mr-1" />
                Cambiar
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setPreview('');
                  setUrlInput('');
                }}
              >
                <Link2 className="w-4 h-4 mr-1" />
                Cambiar URL
              </Button>
            )}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
              <div className="animate-spin w-8 h-8 border-4 border-canary border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>
      ) : mode === 'upload' ? (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full h-48 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-royal hover:bg-slate-50 transition-colors"
        >
          {uploading ? (
            <div className="animate-spin w-8 h-8 border-4 border-canary border-t-transparent rounded-full"></div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-slate-400" />
              <span className="text-sm text-slate-500">Haz clic para subir una imagen</span>
              <span className="text-xs text-slate-400">PNG, JPG hasta 5MB</span>
            </>
          )}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://ejemplo.com/imagen.jpg"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              className="flex-1"
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              className="bg-royal text-white hover:bg-royal-hover"
            >
              Usar
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Pega la URL de una imagen de internet (ej: de Amazon, Mercado Libre, etc.)
          </p>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
